import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import FocusLock from 'react-focus-lock';
import Button from '@atlaskit/button';
import Form from '@atlaskit/form';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useFeedbackCollector } from '@atlassian/jira-business-feedback-collector';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags/src/services';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { FEEDBACK_GROUP_ID } from '@atlassian/jira-project-create-importer-common';

import { X2COnboarding } from '@atlassian/jira-project-create-importer-onboarding';
import { X2C_SCREENS } from '../../common/constants/metrics';
import type { X2CImportValidationError } from '../../common/types';
import { StepFooter } from '../../common/ui/step-footer';
import { hasMissingFields, hasTableColumnErrors } from '../../common/utils';
import {
	useMappingState,
	useAdvancedOptions,
	useMigrationId,
	useNumVisibleUnmappedColumns,
	useProjectData,
} from '../../controllers/import-state';
import { useSource } from '../../controllers/import-state/selectors';
import { useStartImportController } from '../../controllers/start-import';
import { useValidateX2CImport } from '../../services/validate-import';
import { VALIDATION_FAILED_FLAG } from './constants';
import { FieldsDropdown } from './fields-dropdown';
import { Header } from './header';
import { CSVFieldMappingScreen } from './main';
import { MappingAdvancedOptions } from './mapping-advanced-options';
import MappingTable from './mapping-table';
import messages from './messages';
import { UnmappedColumnsBanner } from './unmapped-columns-banner';
import { UnmappedColumnsWarningModal } from './unmapped-columns-warning-modal';
import { ValidationError } from './validation-error';

export type MappingScreenProps = {
	onNext: () => void;
	onReturn: () => void;
	onCloseAndReturnToUploadStep: () => void;
	shouldStartImportOnNext: boolean;
	showLearnMoreLink?: boolean;
	isCompact?: boolean;
	hideFeedback?: boolean;
	rightAlignFooter?: boolean;
	offsetModalY?: number;
};

export const OldMappingScreen = ({
	onNext,
	onReturn,
	shouldStartImportOnNext,
	onCloseAndReturnToUploadStep,
	showLearnMoreLink,
	isCompact,
	hideFeedback,
	rightAlignFooter,
	offsetModalY,
}: MappingScreenProps) => {
	const { formatMessage } = useIntl();
	const [mappingState, { setMappingStateValidationError, clearSelectedColumn }] = useMappingState();
	const openFeedbackModal = useFeedbackCollector();
	const [migrationId] = useMigrationId();
	const [advancedOptions] = useAdvancedOptions();
	const { showFlag } = useFlagService();
	const [numVisibleUnmappedColumns] = useNumVisibleUnmappedColumns();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { startImport, isStartImportDisabled, hasImportErrorOccurred } = useStartImportController();
	const [projectData] = useProjectData();

	const [isUnmappedWarningModalOpen, setIsUnmappedWarningModalOpen] = useState(false);

	const onSubmitFeedbackClick = useCallback(() => {
		openFeedbackModal(FEEDBACK_GROUP_ID);
	}, [openFeedbackModal]);

	const [source] = useSource();
	const { isLoading, validateX2CImport } = useValidateX2CImport(source);

	const onValidateFetchFail = useCallback(() => {
		showFlag(VALIDATION_FAILED_FLAG);
	}, [showFlag]);

	const handleOnNext = useCallback(() => {
		clearSelectedColumn();
		shouldStartImportOnNext ? startImport({ projectData, onNext }) : onNext();
	}, [clearSelectedColumn, onNext, projectData, shouldStartImportOnNext, startImport]);

	const handleOnReturn = useCallback(() => {
		clearSelectedColumn();
		onReturn();
	}, [clearSelectedColumn, onReturn]);

	const onValidationError = useCallback(
		(err: X2CImportValidationError) => {
			setMappingStateValidationError(err);
			fireTrackAnalytics(createAnalyticsEvent({}), 'columnMappingValidation failed', {
				numberOfColumnErrors: Object.keys(err.columnErrors).length,
				missingFields: err.missingFields,
			});
		},
		[createAnalyticsEvent, setMappingStateValidationError],
	);

	const onCloseUnmappedWarningModal = useCallback(() => {
		setIsUnmappedWarningModalOpen(false);
	}, []);

	const onNextOrOpenWarningModal = useCallback(() => {
		numVisibleUnmappedColumns === 0 ? handleOnNext() : setIsUnmappedWarningModalOpen(true);
	}, [numVisibleUnmappedColumns, handleOnNext]);

	const validateOnContinue = useCallback(() => {
		if (mappingState === null || migrationId === null) {
			return;
		}
		validateX2CImport({
			migrationId,
			mappingState,
			advancedOptions,
			onSuccess: onNextOrOpenWarningModal,
			onFetchFail: onValidateFetchFail,
			onValidationError,
		});
	}, [
		mappingState,
		migrationId,
		validateX2CImport,
		advancedOptions,
		onNextOrOpenWarningModal,
		onValidateFetchFail,
		onValidationError,
	]);

	const hasValidationErrors = hasTableColumnErrors(mappingState) || hasMissingFields(mappingState);

	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.MAPPING} sourceType={SCREEN}>
			<Wrapper
				data-testid="project-create-importer-upload-flow.ui.mapping-screen.wrapper"
				isCompact={isCompact}
			>
				<StyledFocusLock autoFocus={false}>
					<Header
						onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep}
						isCompactHeader={isCompact}
						offsetModalY={offsetModalY}
					/>
					{hasValidationErrors && <ValidationError showLearnMoreLink={showLearnMoreLink} />}
					<RowWrapper>
						<UnmappedColumnsBanner hasValidationErrors={hasValidationErrors} />
						<FieldsDropdown />
					</RowWrapper>
					<MappingTable />
					<Form onSubmit={validateOnContinue}>
						{({ formProps }) => (
							<form
								{...formProps}
								data-testid="project-create-importer-upload-flow.ui.mapping-screen.form"
							>
								<FooterWrapper>
									<ContentWrapper>
										<MappingAdvancedOptions />
										{!hideFeedback && (
											<Button
												testId="project-create-importer-upload-flow.ui.mapping-screen.feedback-button"
												appearance="link"
												spacing="compact"
												onClick={onSubmitFeedbackClick}
												iconBefore={<CommentIcon label="" />}
											>
												{formatMessage(messages.submitFeedbackCallToAction)}
											</Button>
										)}
									</ContentWrapper>
									<StepFooter
										onReturn={handleOnReturn}
										shouldStartImportOnNext={shouldStartImportOnNext}
										isLoading={isLoading}
										shouldDisableNext={
											shouldStartImportOnNext ? isStartImportDisabled : mappingState === null
										}
										nextButtonType="submit"
										hasImportErrorOccurred={hasImportErrorOccurred}
										alignRight={rightAlignFooter}
									/>
								</FooterWrapper>
							</form>
						)}
					</Form>
					<UnmappedColumnsWarningModal
						isModalOpen={isUnmappedWarningModalOpen}
						onCancel={onCloseUnmappedWarningModal}
						onContinue={handleOnNext}
						offsetModalY={offsetModalY}
					/>
					<FireScreenAnalytics />
					{mappingState !== null && <X2COnboarding />}
				</StyledFocusLock>
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

export const MappingScreen = (props: MappingScreenProps) => {
	if (fg('enable-x2c-standardised-flow')) {
		return <CSVFieldMappingScreen {...props} />;
	}

	return <OldMappingScreen {...props} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ isCompact?: boolean }>({
	display: 'flex',
	flexFlow: 'column',
	width: '100%',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: ({ isCompact }) =>
		`${token('space.300', '24px')} ${isCompact ? 0 : token('space.1000', '80px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isCompact }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		isCompact ? 'inherit' : token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ isCompact }) => (isCompact ? '100%' : 'auto'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	flex: '0 1 240px',
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'baseline',
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	maxHeight: '100%',
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledFocusLock = styled(FocusLock)({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
});
